// /frontend/src/pages/CustomDesign.jsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import Layout from '../components/layout/Layout';
import axios from 'axios';
import { toast } from 'react-toastify';
import { uploadService } from '../services/uploadService'

const CustomDesign = () => {
  const navigate = useNavigate();
  const { isAuthenticated, loading: authLoading, user } = useSelector(state => state.auth);
  
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragActive, setDragActive] = useState(false);

  // Check if user is authenticated when component mounts
  useEffect(() => {
    if (!authLoading && !isAuthenticated) {
      // Store current page in localStorage to redirect back after login
      localStorage.setItem('redirectAfterLogin', '/custom-design');
      navigate('/login');
      toast.info('Please login to access Design services');
    }
  }, [isAuthenticated, authLoading, navigate]);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = (file) => {
    // Check file type - accept only PDFs, images, and CAD files
    const acceptedTypes = ['application/pdf', 'image/png', 'image/jpeg', 'image/jpg', 'application/dxf', 'application/dwg'];
    if (!acceptedTypes.includes(file.type) && !file.name.endsWith('.dwg') && !file.name.endsWith('.dxf')) {
      toast.error('Please upload a valid floor plan file (PDF, PNG, JPG, DWG, or DXF)');
      return;
    }
    
    // Check file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toast.error('File size must be less than 10MB');
      return;
    }
    
    setFile(file);
    toast.success(`File "${file.name}" selected successfully`);
  };

  const handleGetStarted = () => {
    if (!isAuthenticated) {
      localStorage.setItem('redirectAfterLogin', '/custom-design');
      navigate('/login');
      return;
    }
    
    // If no floor plan yet, scroll to upload section
    if (!file) {
      document.getElementById('floor-plan-upload').scrollIntoView({ behavior: 'smooth' });
      toast.info('Please upload your floor plan to get started');
      return;
    }
    
    handleUpload();
  };

  const handleUpload = async () => {
    if (!file) return;
    
    try {
      setUploading(true);
      
      // Create form data
      const formData = new FormData();
      formData.append('floorPlan', file);
      
      // Use the uploadService instead of direct axios call
      const response = await uploadService.uploadFloorPlan(formData, (percentCompleted) => {
        setUploadProgress(percentCompleted);
      });
      
      toast.success('Floor plan uploaded successfully!');
      
      // Redirect to confirmation page with the design request ID
      setTimeout(() => {
        navigate(`/custom-design/confirmation/${response.data._id}`);
      }, 1000);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.message || 'Error uploading floor plan');
    } finally {
      setUploading(false);
    }
  };

  if (authLoading) {
    return (
      <Layout>
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-teal-500"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-3xl font-bold mb-6">Design Services</h1>
        <p className="text-lg text-gray-700 mb-6">
          Work with our professional designers to create your perfect space with personalized revisions.
        </p>
        
        {/* How It Works section */}
        <div className="bg-teal-50 border border-teal-200 rounded-lg p-6 mb-8">
          <h2 className="text-xl font-semibold text-teal-800 mb-3">How It Works</h2>
          <ol className="list-decimal pl-5 space-y-3">
            <li>Submit your design requirements and inspiration</li>
            <li>Our designers will create a custom concept for your space</li>
            <li>Review the design and request up to 2 rounds of revisions</li>
            <li>Approve the final design and proceed to ordering</li>
          </ol>
        </div>
        
        {/* Collaborative Build Your Own Package */}
        <div className="bg-white border border-gray-200 rounded-lg p-6 mb-8 shadow-sm">
          <div className="flex items-center mb-4">
            <div className="bg-teal-800 text-white rounded-full w-8 h-8 flex items-center justify-center mr-3">
              <span className="font-semibold">2</span>
            </div>
            <h2 className="text-2xl font-semibold">Collaborative 'Build Your Own Package'</h2>
          </div>
          
          <p className="text-lg mb-4">
            Work with the Henderson Design Team to curate your furnishings. Our professional designers will collaborate with you to create a personalized space that reflects your style and needs.
          </p>
          
          <div className="bg-gray-50 p-4 rounded-lg mb-5">
            <h3 className="font-semibold text-lg mb-2">Package Includes:</h3>
            <ul className="list-disc pl-5 space-y-2">
              <li>Personalized consultation with a Henderson designer</li>
              <li>Custom space plan based on your uploaded floor plan</li>
              <li>Curated furniture and decor selections</li>
              <li>Two rounds of revisions to perfect your design</li>
            </ul>
          </div>
          
          <div className="flex flex-col md:flex-row md:items-center justify-between bg-teal-50 p-4 rounded-lg">
            <div>
              <p className="font-semibold text-teal-800">$500 design fee due upfront</p>
              <p className="text-sm text-gray-600">Client is responsible to verify that the floor plan provided is exact to as built conditions.</p>
            </div>
            <button 
              className="mt-4 md:mt-0 bg-teal-800 text-white px-6 py-2 rounded hover:bg-teal-900 transition-all"
              onClick={handleGetStarted}
              disabled={uploading}
            >
              {uploading ? 'Processing...' : 'Get Started'}
            </button>
          </div>
        </div>
        
        {/* Upload floor plan section */}
        <div id="floor-plan-upload" className="border border-gray-200 rounded-lg p-6">
          <h2 className="text-xl font-semibold mb-4">Ready to begin? Upload your floor plan</h2>
          <div 
            className={`border-2 border-dashed rounded-lg p-8 text-center ${
              dragActive ? 'border-teal-500 bg-teal-50' : 'border-gray-300'
            } ${file ? 'bg-teal-50' : ''}`}
            onDragEnter={handleDrag}
            onDragLeave={handleDrag}
            onDragOver={handleDrag}
            onDrop={handleDrop}
          >
            {!file ? (
              <>
                <svg className="mx-auto h-12 w-12 text-gray-400" stroke="currentColor" fill="none" viewBox="0 0 48 48" aria-hidden="true">
                  <path d="M28 8H12a4 4 0 00-4 4v20m32-12v8m0 0v8a4 4 0 01-4 4H8m36-12h-4m4 0H20" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                </svg>
                <p className="mt-2 text-sm text-gray-600">Drag and drop your floor plan file here, or click to browse</p>
                <p className="text-xs text-gray-500 mt-1">Accepted formats: PDF, PNG, JPG, DWG, DXF (max 10MB)</p>
                <input
                  type="file"
                  id="floor-plan-input"
                  className="hidden"
                  accept=".pdf,.png,.jpg,.jpeg,.dwg,.dxf"
                  onChange={handleFileChange}
                />
                <button 
                  className="mt-4 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-teal-700 bg-teal-100 hover:bg-teal-200"
                  onClick={() => document.getElementById('floor-plan-input').click()}
                >
                  Upload Floor Plan
                </button>
              </>
            ) : (
              <div className="space-y-3">
                <div className="flex items-center justify-center">
                  <svg className="h-8 w-8 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7"></path>
                  </svg>
                </div>
                <p className="font-medium text-teal-800">{file.name}</p>
                <p className="text-sm text-gray-600">{(file.size / (1024 * 1024)).toFixed(2)} MB</p>
                
                {uploading ? (
                  <div className="w-full max-w-md mx-auto mt-2">
                    <div className="w-full bg-gray-200 rounded-full h-2.5">
                      <div className="bg-teal-600 h-2.5 rounded-full" style={{ width: `${uploadProgress}%` }}></div>
                    </div>
                    <p className="text-xs text-gray-600 mt-1">Uploading: {uploadProgress}%</p>
                  </div>
                ) : (
                  <div className="flex space-x-3 justify-center">
                    <button 
                      className="px-3 py-1 bg-gray-200 hover:bg-gray-300 rounded text-sm"
                      onClick={() => setFile(null)}
                    >
                      Change File
                    </button>
                    <button 
                      className="px-3 py-1 bg-teal-600 hover:bg-teal-700 text-white rounded text-sm"
                      onClick={handleUpload}
                    >
                      Upload & Continue
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CustomDesign;